import { LineChart } from "@mui/x-charts";
import { SeriesValueFormatterContext } from "@mui/x-charts/models/seriesType/common";
import { normalSanatizerBRL } from "src/utils/functions";
import { useState, useEffect } from "react";

function valueFormatter(value: string | number) {
  let response = Number(value).toFixed(0).replace(".", ",");
  response = response.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");

  if (response.length > 5) {
    response = response.slice(0, -4) + "k";
  }
  return response;
}

const aggregateYearlyData = (monthlyData: number[]): number[] => {
  const yearlyData = [];
  const monthsInYear = 12;
  yearlyData.push(monthlyData[0]);

  for (let i = monthsInYear; i < monthlyData.length; i += monthsInYear) {
    if (i + monthsInYear >= monthlyData.length) {
      yearlyData.push(monthlyData[monthlyData.length - 1]);
    } else {
      yearlyData.push(monthlyData[i]);
    }
  }
  return yearlyData;
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

export function PrimeiroMilhaoGraph({
  results,
}: {
  results: PrimeiroMilhaoResultsType;
}) {
  const isMobile = useIsMobile();
  
  const isHighDataVolume = results.monthlyTax.length > 60;
  const displayMonthlyData = !isHighDataVolume;

  const dataAcumulatedInterest = displayMonthlyData
    ? results.monthlyAcumulatedInterest
    : aggregateYearlyData(results.monthlyAcumulatedInterest);

  const dataInvested = displayMonthlyData
    ? results.monthlyInvested.slice(0, -1)
    : aggregateYearlyData(results.monthlyInvested.slice(0, -1));

  const dataInterest = displayMonthlyData
    ? results.monthlyResult
    : aggregateYearlyData(results.monthlyResult);

  const chartConfig = {
    height: isMobile ? "350px" : "500px",
    margin: {
      top: isMobile ? 100 : 80,      // Mais espaço para legenda no mobile
      right: isMobile ? 20 : 30,
      bottom: isMobile ? 60 : 50,
      left: isMobile ? 50 : 60,
    },
    legend: {
      direction: (isMobile ? "column" : "row") as "column" | "row",
      position: { 
        vertical: "top" as const, 
        horizontal: (isMobile ? "left" : "middle") as "left" | "middle"
      },
      padding: isMobile ? 15 : 20,
      itemGap: isMobile ? 12 : 20,
      itemMarkWidth: isMobile ? 10 : 14,
      itemMarkHeight: isMobile ? 10 : 14,
      labelStyle: {
        fontSize: isMobile ? "11px" : "12px",
        fontWeight: "500",
      },
    }
  };

  return (
    <div className={`w-full min-w-[300px] min-h-[300px] overflow-auto -ml-2 sm:ml-0`}>
      <div style={{ height: chartConfig.height, width: "100%" }}>
        <LineChart
          colors={["#8d0202", "#000", "#D4A017", "#9CA3AF"]}
          grid={{ vertical: false, horizontal: true }}
          
          margin={chartConfig.margin}
          
          yAxis={[
            {
              valueFormatter: (value) => valueFormatter(value),
            },
          ]}
          
          slotProps={{
            legend: chartConfig.legend,
          }}
          
          series={[
            {

              data: dataAcumulatedInterest,
              valueFormatter: (
                value: number | null,
                context: SeriesValueFormatterContext
              ) => {
                if (value === null) {
                  return "N/A";
                }
                return "R$ " + normalSanatizerBRL(Number(value.toString()));
              },
              label: "Total Acumulado",
            },
            {

              data: dataInvested,
              valueFormatter: (value: number | null) => {
                if (value === null) {
                  return "N/A";
                }
                return "R$ " + normalSanatizerBRL(Number(value.toFixed(2))).toString();
              },
              label: "Valor Investido",
            },
            {

              data: dataInterest,
              valueFormatter: (value: number | null) => {
                if (value === null) {
                  return "N/A";
                }
                return "R$ " + normalSanatizerBRL(Number(value.toFixed(2))).toString();
              },
              label: "Total em Juros",
            },
            {

              data: Array(dataAcumulatedInterest.length).fill(1000000),
              valueFormatter: () => "Meta: R$ 1.000.000,00",
              label: "Meta R$ 1 Milhão",
            },
          ]}
        />
      </div>
      
      {/* Footer informativo responsivo */}
      <div className="mt-4 pt-4 border-t bg-gray-50 rounded-b px-4 pb-2">
        <div className="text-xs text-gray-600 text-center">
          <div className="mb-1">
             {displayMonthlyData
              ? `Visualização mensal • ${results.monthlyAcumulatedInterest.length} meses analisados`
              : `Visualização anual • ${Math.ceil(results.monthlyAcumulatedInterest.length / 12)} anos analisados`}
          </div>
          <div className="font-medium text-gray-700">
             {results.goalReached
              ? `Meta atingida em ${Math.floor(results.timeToGoal.totalMonths / 12)} anos${results.timeToGoal.totalMonths % 12 > 0 ? ` e ${results.timeToGoal.totalMonths % 12} meses` : ""}`
              : "Projeção até atingir a meta"}
          </div>
          
          {/* Indicativo adicional da meta para mobile */}
          {isMobile && (
            <div className="text-xs text-gray-500 mt-2">
              Esta linha representa a meta de R$ 1 milhão
            </div>
          )}
          
          {/* Indicativo da meta para desktop */}
          {!isMobile && (
            <div className="text-xs text-gray-500 mt-1">
              Meta de R$ 1 milhão • Passe o mouse sobre o gráfico para detalhes
            </div>
          )}
        </div>
      </div>
    </div>
  );
}